import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import MapFiltersSection from '../../road-network-editor/RoadNetworkManager/MapFilters/MapFiltersSection';
import '../../road-network-editor/RoadNetworkManager/MapFilters/map-filters.scss';
import * as CheckboxField from '@components/form/CheckboxField/CheckboxField';
import { Form } from '@components/form/Form/Form';
import { getFilledFieldsObjOnFormSubmit } from '@utils/utils';
import { LoadingComponent } from '@components/LoadingComponent/LoadingComponent';
import { useMapFiltersContext } from '@context/mapFiltersContext';
import { MapFiltersType, TemporaryMapFilterType } from '../types';
import SliderField from '@components/form/SliderField/SliderField';

const MapTransitFilters = () => {
    const {
        loading,
        setLoading,
        enabledFilters,
        setEnabledFilters,
        filters,
        setFilters,
        resetFilters,
        setMapFilter,
    } = useMapFiltersContext();
    const { t } = useTranslation();

    const departuresDefaultValue = useMemo(() => {
        return filters['departuresNumMin'] && filters['departuresNumMax']
            ? [filters['departuresNumMin'], filters['departuresNumMax']]
            : [1, 10];
    }, [filters]);

    const getMapFilter = useCallback(
        (enabledFilters: MapFiltersType, filters: TemporaryMapFilterType) => {
            const { modes, departuresNumMin, departuresNumMax, vehicleTypeNames } = filters;

            const _filter: any[] = ['all'];
            if (enabledFilters.modes.length) {
                const modesFilters = (['any'] as any).concat(
                    enabledFilters.modes
                        .filter(mode => modes[mode])
                        .map(mode => ['==', mode, ['get', 'mode']])
                );
                _filter.push(modesFilters);
            }
            if (departuresNumMin !== undefined) {
                _filter.push(['>=', ['get', 'departures'], departuresNumMin]);
            }
            if (departuresNumMax !== undefined) {
                _filter.push(['<=', ['get', 'departures'], departuresNumMax]);
            }
            if (enabledFilters.vehicleTypeNames.length) {
                const highwayTypesFilter = enabledFilters.vehicleTypeNames.filter(
                    highwayType => vehicleTypeNames[highwayType]
                );
                _filter.push(['in', ['get', 'vehicleTypeName'], ['literal', highwayTypesFilter]]);
            }

            return _filter.length > 1 ? _filter : null;
        },
        []
    );

    const onFormSubmit = useCallback(
        async event => {
            const formDataObj = getFilledFieldsObjOnFormSubmit(event);

            const modes = enabledFilters['modes'].reduce((acc, name) => {
                acc[name] = formDataObj[name] === 'true';
                return acc;
            }, {});

            const [departuresNumMin, departuresNumMax] = JSON.parse(
                formDataObj['departures'] as string
            );

            const vehicleTypeNames = (enabledFilters as MapFiltersType).vehicleTypeNames.reduce(
                (acc, name) => {
                    acc[name] = formDataObj[name] === 'true';
                    return acc;
                },
                {}
            );

            const filters: TemporaryMapFilterType = {
                modes,
                departuresNumMin,
                departuresNumMax,
                vehicleTypeNames,
            };

            const mapFilter = getMapFilter(enabledFilters as MapFiltersType, filters);
            setMapFilter(mapFilter);
            setFilters(filters);
        },
        [setMapFilter, enabledFilters]
    );

    return (
        <div className="map-filters">
            <LoadingComponent isLoading={loading}>
                <div className="map-filters__header">
                    <button onClick={resetFilters}>{t('map-filters.reset-filters-btn')}</button>
                </div>
                <Form
                    name="mapFiltersForm"
                    onSubmit={onFormSubmit}
                    submitBtnTitle={t('map-filters.apply-filters-btn')}
                    testId="mapFiltersForm"
                    submitBtnTestId="mapFiltersFormSubmitBtn"
                >
                    <MapFiltersSection title={t('map-filters.transport-modes-group')}>
                        <div className="map-filters__modes-section">
                            {enabledFilters['modes']?.map((name, idx) => {
                                if (name) {
                                    return (
                                        <CheckboxField.Labeled
                                            key={idx}
                                            inline={true}
                                            name={name}
                                            checked={filters['modes'][name]}
                                            alignCheckboxRight
                                            testId={`${name}_mode_checkbox`}
                                        />
                                    );
                                }
                            })}
                        </div>
                    </MapFiltersSection>
                    <MapFiltersSection title={t('map-filters.departures')}>
                        <SliderField
                            name="departures"
                            min={1}
                            max={100}
                            valueLabelDisplay="auto"
                            step={1}
                            defaultValue={departuresDefaultValue}
                            required
                        />
                    </MapFiltersSection>
                    <MapFiltersSection title={t('map-filters.vehicle-type')}>
                        <div className="map-filters__modes-section">
                            {(enabledFilters as MapFiltersType).vehicleTypeNames?.map(
                                (name, idx) => {
                                    return (
                                        <CheckboxField.Labeled
                                            key={idx}
                                            inline={true}
                                            name={name}
                                            checked={
                                                (filters as unknown as MapFiltersType)
                                                    .vehicleTypeNames[name]
                                            }
                                            alignCheckboxRight
                                        />
                                    );
                                }
                            )}
                        </div>
                    </MapFiltersSection>
                </Form>
            </LoadingComponent>
        </div>
    );
};

export default MapTransitFilters;
